import { Component, Input, Output, OnChanges, OnInit, SimpleChanges, EventEmitter, ElementRef, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from '@env/environment';
import { fuseAnimations } from '@fuse/animations';
import { Annotation, Course } from '@shared/models';
import { InternalAnalyticsService } from '@shared/services/internalAnalytics.service';

@Component({
  selector: 'simulation-preview',
  templateUrl: './simulation-preview.component.html',
  styleUrls: ['./simulation-preview.component.scss'],
  animations: fuseAnimations
})
export class SimulationPreviewComponent implements OnInit, OnChanges {
  @Input() course: Course;
  @Input() npc: Annotation;
  @Output() navigateToAssessment = new EventEmitter<void>();
  @Output() expandChange = new EventEmitter<boolean>();
  @ViewChild('simulationPreview') simulationPreviewElement: ElementRef;

  public simulationPreviewUrl: SafeResourceUrl;
  public simulationPreviewLoaded: boolean;
  public simulationPreviewLoadError: boolean;
  private _simulationPreviewLoaded: Promise<boolean>;
  private _simulationPreviewLoadedResolve: (value: boolean | PromiseLike<boolean>) => void;
  private PREVIEW_EVENTS = {
    LOADED: 'player-preview::loaded',
    ERROR: 'player-preview::load-error',
    UPDATE_NPC_ERROR: 'player-preview::update_npc_error',
    UPDATE_SKYBOX: 'player-preview::update_skybox',
    UPDATE_ASSESSMENT: 'player-preview::update_assessment',
    UPDATE_NPC_LABEL: 'player-preview::update_npc_label',
    UPDATE_NPC_MODEL: 'player-preview::update_npc_model',
    UPDATE_NPC_PROMPT: 'player-preview::update_npc_prompt',
    UPDATE_NPC_OUTPUT_VOICE: 'player-preview::update_npc_output_voice',
    UPDATE_NPC_INPUT_LANGUAGE: 'player-preview::update_npc_input_language',
    UPDATE_NPC_INTRO_MESSAGE: 'player-preview::update_npc_intro_message',
    NAVIGATE_TO_ASSESSMENT: 'player-preview::navigate_to_assessment',
    REQUEST_EXPAND: 'player-preview::request-expand',
    REQUEST_COLLAPSE: 'player-preview::request-collapse',
    LOG_INTERCOM_EVENT: 'player-preview::log_intercom_event'
  };
  constructor(private sanitizer: DomSanitizer, private _internalAnalyticsService: InternalAnalyticsService) {}

  ngOnInit(): void {
    this.onMessage = this.onMessage.bind(this);
    this.simulationPreviewLoaded = false;

    this._simulationPreviewLoaded = new Promise((resolve) => {
      this._simulationPreviewLoadedResolve = resolve;
    });

    window.addEventListener('message', this.onMessage);
    const url = `${environment.roomUrl}ai.html?course=${this.course.id}&preview=true`;
    this.simulationPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }
  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.course && changes.course.isFirstChange()) || (changes.npc && changes.npc.isFirstChange()) || !this.simulationPreviewLoaded) return;
    if (changes.course && !!changes.course.currentValue) {
      const newCourse: Partial<Course> = changes.course.currentValue;
      const previousCourse: Partial<Course> = changes.course.previousValue;
      if (newCourse.course_skybox.id !== previousCourse.course_skybox.id) {
        this.postMessage(this.PREVIEW_EVENTS.UPDATE_SKYBOX, { media: newCourse.course_skybox });
      }
      if (newCourse.information.assessment.enabled !== previousCourse.information.assessment.enabled) {
        this.postMessage(this.PREVIEW_EVENTS.UPDATE_ASSESSMENT, { enabled: newCourse.information.assessment.enabled });
      }
    }
    if (changes.npc && !!changes.npc.currentValue) {
      const newNPC: Partial<Annotation> = changes.npc.currentValue;
      const previousNPC: Partial<Annotation> = changes.npc.previousValue;
      if (newNPC.label !== previousNPC.label) {
        this.postMessage(this.PREVIEW_EVENTS.UPDATE_NPC_LABEL, { label: newNPC.label });
      }
      if (newNPC.media_id !== previousNPC.media_id) {
        this.postMessage(this.PREVIEW_EVENTS.UPDATE_NPC_MODEL, { media: newNPC.media });
      }
      if (newNPC.properties.voiceId !== previousNPC.properties.voiceId) {
        this.postMessage(this.PREVIEW_EVENTS.UPDATE_NPC_OUTPUT_VOICE, { voiceId: newNPC.properties.voiceId });
      }
      if (newNPC.properties.languageISOCode !== previousNPC.properties.languageISOCode) {
        this.postMessage(this.PREVIEW_EVENTS.UPDATE_NPC_INPUT_LANGUAGE, { languageISOCode: newNPC.properties.languageISOCode });
      }
      if (newNPC.properties.introMessage !== previousNPC.properties.introMessage) {
        this.postMessage(this.PREVIEW_EVENTS.UPDATE_NPC_INTRO_MESSAGE, { introMessage: newNPC.properties.introMessage });
      }
      const newDefiningConversation = newNPC.properties.definingConversation.messages;
      const newPrompt =
        newDefiningConversation && newDefiningConversation.find((m: any) => m.role === 'system') && newDefiningConversation.find((m: any) => m.role === 'system').content;
      const previousDefiningConversation = previousNPC.properties.definingConversation.messages;
      const previousPrompt =
        previousDefiningConversation &&
        previousDefiningConversation.find((m: any) => m.role === 'system') &&
        previousDefiningConversation.find((m: any) => m.role === 'system').content;

      if (newPrompt !== previousPrompt) {
        this.postMessage(this.PREVIEW_EVENTS.UPDATE_NPC_PROMPT, { prompt: newPrompt });
      }
    }
  }
  ngOnDestroy(): void {
    window.removeEventListener('message', this.onMessage);
    this._simulationPreviewLoaded = null;
  }

  postMessage(event: string, data: any): void {
    const targetDomain = getOrigin(environment.roomUrl);
    this.simulationPreviewElement.nativeElement.contentWindow.postMessage({ event, data }, targetDomain);
  }

  async onMessage(message: MessageEvent): Promise<void> {
    if (!hasSameOrigin(message.origin, environment.roomUrl)) {
      return;
    }

    const { event, data } = message.data;
    if (event === this.PREVIEW_EVENTS.LOADED) {
      this._simulationPreviewLoadedResolve(event);
      this.simulationPreviewLoaded = true;
    } else if (event === this.PREVIEW_EVENTS.ERROR) {
      this.simulationPreviewLoadError = true;
    } else if (event === this.PREVIEW_EVENTS.NAVIGATE_TO_ASSESSMENT) {
      this.navigateToAssessment.emit();
    } else if (event === this.PREVIEW_EVENTS.REQUEST_EXPAND) {
      this.expandChange.emit(true);
    } else if (event === this.PREVIEW_EVENTS.REQUEST_COLLAPSE) {
      this.expandChange.emit(false);
    } else if (event === this.PREVIEW_EVENTS.LOG_INTERCOM_EVENT) {
      this._internalAnalyticsService.trackPreviewEvent(data);
    } else if (event === this.PREVIEW_EVENTS.UPDATE_NPC_ERROR) {
      // TODO: Define what happens in case of errors
    }
  }
}

function hasSameOrigin(url: string, urlToCompare: string): boolean {
  return getOrigin(url) === getOrigin(urlToCompare);
}

function getOrigin(urlString: string): string {
  const url = new URL(urlString);
  return url.origin;
}
